import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { LoadingState } from "components/common/LoadingState";
import { CodeEditor } from "components/common/CodeEditor/CodeEditor";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { getMaintenanceGuide, putMaintenanceGuide } from "@/lib/api/datasets";
import {
  approxTokens,
  formatGuideSize,
  utf8Bytes,
  MAX_GUIDE_BYTES,
} from "@/components/main/datasets/maintenanceGuideFormat";

const formatUpdated = (unixMillis: number, by?: string): string => {
  const date = new Date(unixMillis).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return by ? `updated ${date} by ${by}` : `updated ${date}`;
};

interface MaintenanceGuideDialogProps {
  datasetId: string;
  datasetName: string;
  open: boolean;
  onClose: () => void;
  /// Fired after a successful save or clear, so the doorway row can refresh
  /// its status line.
  onSaved: () => void;
}

/// The maintenance-guide editor: the one place the guide body is ever
/// fetched. Plain markdown with syntax highlighting, no rendered preview —
/// the guide's consumer is an agent reading raw text, so the author should
/// see what the agent sees.
export const MaintenanceGuideDialog: React.FC<MaintenanceGuideDialogProps> = ({
  datasetId,
  datasetName,
  open,
  onClose,
  onSaved,
}) => {
  const [text, setText] = useState("");
  const [initial, setInitial] = useState("");
  const [provenance, setProvenance] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    getMaintenanceGuide(datasetId)
      .then((g) => {
        if (cancelled) return;
        setText(g?.guide ?? "");
        setInitial(g?.guide ?? "");
        setProvenance(
          g ? formatUpdated(g.updated_at, g.updated_by_display) : null,
        );
      })
      .catch(() => {
        if (!cancelled) showNotificationGlobal("Failed to load guide", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, datasetId]);

  const bytes = useMemo(() => utf8Bytes(text), [text]);
  const overCap = bytes > MAX_GUIDE_BYTES;
  const dirty = text !== initial;

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await putMaintenanceGuide(datasetId, text.trim() ? text : null);
      onSaved();
      onClose();
    } catch {
      showNotificationGlobal("Failed to save maintenance guide", "error");
    } finally {
      setSaving(false);
    }
  }, [datasetId, text, onSaved, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="wide"
      // Center over the CONTENT area: the backdrop spans the viewport, so
      // without this offset the dialog reads as left-shifted next to the
      // sidebar. The var is published by MainLayout and tracks collapse.
      backdropStyle={{ paddingLeft: "var(--main-sidebar-width, 0px)" }}
      // The wide Dialog pads its title and footer (px-5) but not its body —
      // this dialog owns the body layout entirely.
      autoSpacing={false}
      title={
        <div className="min-w-0">
          <p className="text-fg-bold">Maintenance guide</p>
          <p
            className="text-fg-muted truncate"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--font-size-small)",
            }}
          >
            {datasetName}
            {provenance ? ` · ${provenance}` : ""}
          </p>
        </div>
      }
      actions={
        <>
          <span
            data-testid="guide-byte-counter"
            className={`mr-auto ${overCap ? "text-error" : "text-fg-muted"}`}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--font-size-small)",
            }}
          >
            ~{approxTokens(bytes).toLocaleString()} tokens ·{" "}
            {formatGuideSize(bytes)} of {formatGuideSize(MAX_GUIDE_BYTES)}
          </span>
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            disabled={loading || overCap || !dirty}
            loading={saving}
          >
            Save
          </Button>
        </>
      }
    >
      {/* The Dialog root forces select-none/cursor-default — right for chrome,
          wrong for an editing surface. Re-enable both for the body. */}
      <div className="flex flex-col gap-3 px-5 py-4 flex-1 min-h-0 select-text cursor-auto">
        <p
          className="text-fg-muted"
          style={{ fontSize: "var(--font-size-small)", maxWidth: "72ch" }}
        >
          Tell your AI agents how to keep this dataset up to date. An agent
          connected via MCP reads this guide before changing the data. It stays
          private to you and your agents — people you share the dataset with
          never see it.
        </p>
        {loading ? (
          <LoadingState label="Loading guide…" />
        ) : (
          <div className="rounded-app border border-border overflow-hidden min-h-0 cursor-text">
            <CodeEditor
              value={text}
              onChange={setText}
              language="markdown"
              height="auto"
              minHeight={280}
              maxHeight={480}
              // Short enough to fit unclamped: placeholders are ellipsis-cut
              // at one line (a wrapped one doubles the native caret).
              placeholder="How is this dataset kept current? Sources, cadence, duplicates, what never to edit — not schema."
              autoFocus
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
